import { useState } from "react";
import { toast } from "sonner";
import { api, yen, errMsg, PAYMENTS } from "../lib/api";
import { useStore } from "../lib/store";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Wallet, Printer } from "lucide-react";

const line = "--------------------------------";

const ShiftSlip = ({ data }) => {
  const { storeName, address, phone } = useStore();
  return (
    <div id="thermal-receipt">
      <div style={{ textAlign: "center" }}>
        <div style={{ fontWeight: "bold", fontSize: 12 }}>{storeName.toUpperCase()}</div>
        <div>{address}</div>
        <div>Tel {phone}</div>
        <div style={{ fontWeight: "bold" }}>TUTUP KAS HARIAN</div>
      </div>
      <div>{line}</div>
      <div>Kasir : {data.cashier_name}</div>
      <div>Tanggal: {data.date}</div>
      <div>Cetak : {new Date().toLocaleString("ja-JP")}</div>
      <div>{line}</div>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <span>Transaksi</span>
        <span>{data.order_count}</span>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "bold" }}>
        <span>OMZET</span>
        <span>{yen(data.revenue)}</span>
      </div>
      <div>{line}</div>
      {PAYMENTS.map((p) => (
        <div key={p.id} style={{ display: "flex", justifyContent: "space-between" }}>
          <span>{p.label}</span>
          <span>{yen(data.by_method?.[p.id] || 0)}</span>
        </div>
      ))}
      <div>{line}</div>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <span>Tunai sistem</span>
        <span>{yen(data.cash_expected)}</span>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <span>Uang fisik</span>
        <span>{yen(data.counted_cash)}</span>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "bold" }}>
        <span>Selisih</span>
        <span>
          {data.difference > 0 ? "+" : ""}
          {yen(data.difference)}
        </span>
      </div>
      <div>{line}</div>
      <div style={{ textAlign: "center" }}>Tanda tangan kasir</div>
      <div style={{ marginTop: 24, textAlign: "center" }}>______________________</div>
    </div>
  );
};

export const ShiftCloseButton = () => {
  const [open, setOpen] = useState(false);
  const [recap, setRecap] = useState(null);
  const [cash, setCash] = useState("");
  const [closed, setClosed] = useState(null);
  const [busy, setBusy] = useState(false);

  const start = async () => {
    try {
      const { data } = await api.get("/shifts/today");
      setRecap(data);
      setClosed(data.closing || null);
      setCash("");
      setOpen(true);
    } catch (e) {
      toast.error(errMsg(e, "Gagal memuat rekap kas"));
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/shifts/close", {
        counted_cash: Number(cash) || 0,
      });
      setClosed(data);
      toast.success("Kas hari ini ditutup");
    } catch (e) {
      toast.error(errMsg(e, "Gagal menutup kas"));
    } finally {
      setBusy(false);
    }
  };

  const view = closed || recap;
  const diff = (Number(cash) || 0) - (recap?.cash_expected || 0);

  return (
    <>
      <Button variant="outline" data-testid="open-shift-close-btn" onClick={start}>
        <Wallet size={16} className="mr-1" /> Tutup Kas
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="shift-close-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Tutup Kas Harian</DialogTitle>
          </DialogHeader>
          {view && (
            <div className="space-y-3 text-sm">
              <div className="text-xs text-muted-foreground">
                {view.cashier_name} · {view.date}
              </div>
              {recap?.already_closed && (
                <p className="text-xs text-emerald-600" data-testid="shift-already-closed">
                  Kas hari ini sudah ditutup. Anda hanya bisa melihat atau mencetak ulang rekapnya.
                </p>
              )}
              <div className="flex justify-between">
                <span>Transaksi</span>
                <span className="num" data-testid="shift-order-count">
                  {view.order_count}
                </span>
              </div>
              <div className="flex justify-between font-display text-xl font-semibold">
                <span>Omzet</span>
                <span className="num" data-testid="shift-revenue">
                  {yen(view.revenue)}
                </span>
              </div>
              <div className="border-y border-dashed py-3 space-y-2">
                {PAYMENTS.map((p) => (
                  <div key={p.id} className="flex justify-between" data-testid={`shift-method-${p.id}`}>
                    <span className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: p.color }} />
                      {p.label}
                    </span>
                    <span className="num">{yen(view.by_method?.[p.id] || 0)}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between">
                <span>Tunai menurut sistem</span>
                <span className="num" data-testid="shift-cash-expected">
                  {yen(view.cash_expected)}
                </span>
              </div>
              {!closed ? (
                <>
                  <Input
                    data-testid="shift-counted-cash-input"
                    inputMode="numeric"
                    placeholder="Uang fisik di kasir (¥)"
                    value={cash}
                    onChange={(e) => setCash(e.target.value.replace(/[^0-9]/g, ""))}
                  />
                  <div className="flex justify-between">
                    <span>Selisih</span>
                    <span
                      className={`num font-semibold ${
                        diff === 0 ? "text-emerald-600" : "text-primary"
                      }`}
                      data-testid="shift-difference"
                    >
                      {diff > 0 ? "+" : ""}
                      {yen(diff)}
                    </span>
                  </div>
                  <Button
                    className="w-full"
                    disabled={busy || cash === ""}
                    data-testid="confirm-shift-close-btn"
                    onClick={submit}
                  >
                    Tutup Kas & Cetak
                  </Button>
                </>
              ) : (
                <>
                  <div className="flex justify-between">
                    <span>Uang fisik</span>
                    <span className="num">{yen(closed.counted_cash)}</span>
                  </div>
                  <div className="flex justify-between font-semibold">
                    <span>Selisih</span>
                    <span className="num" data-testid="shift-closed-difference">
                      {closed.difference > 0 ? "+" : ""}
                      {yen(closed.difference)}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      data-testid="print-shift-btn"
                      onClick={() => window.print()}
                    >
                      <Printer size={16} className="mr-1" /> Cetak 58mm
                    </Button>
                    <Button
                      className="flex-1"
                      data-testid="close-shift-dialog-btn"
                      onClick={() => setOpen(false)}
                    >
                      Selesai
                    </Button>
                  </div>
                  <ShiftSlip data={closed} />
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
